import { scenarios } from '../data/ledger'
import {
  EvidenceCategory,
  FlagRecord,
  ScenarioId,
  ScenarioResult,
} from '../types/assessment'

export const EVIDENCE_KEY: Record<EvidenceCategory, { label: string; className: string }> = {
  resolved: {
    label: 'Handled and correctly resolved',
    className: 'border-teal-200 bg-teal-50 text-teal-800',
  },
  'clean-verified': {
    label: 'Clean case, verified proportionately',
    className: 'border-slate-200 bg-slate-50 text-slate-700',
  },
  escalated: {
    label: 'Handled and appropriately escalated',
    className: 'border-sky-200 bg-sky-50 text-sky-800',
  },
  'over-or-incorrect': {
    label: 'Over-checked, or resolved incorrectly',
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  missed: { label: 'Missed', className: 'border-slate-300 bg-white text-slate-600' },
}

const isCorrectingAction = (f: FlagRecord) => f.actionType === 'adjusting' || f.actionType === 'reclass'
const mentions = (f: FlagRecord, terms: string[]) => {
  const text = `${f.what} ${f.narrative} ${f.escalateWhy}`.toLowerCase()
  return terms.some((t) => text.includes(t))
}

function evaluate(
  scenarioId: ScenarioId,
  flag: FlagRecord | undefined,
  openedDocs: string[],
): { category: EvidenceCategory; evidence: string } {
  switch (scenarioId) {
    // Clean cases — the correct behaviour is one confirming check and no flag.
    case 'S1':
    case 'S4': {
      const docId = scenarioId === 'S1' ? 'INV-4472' : 'INV-4468'
      const opened = openedDocs.includes(docId)
      if (flag) {
        return {
          category: 'over-or-incorrect',
          evidence: `Raised on a clean line. ${docId} was ${
            opened ? 'opened' : 'never opened'
          }; the line agrees to the supporting invoice.`,
        }
      }
      return opened
        ? {
            category: 'clean-verified',
            evidence: `Accepted after opening ${docId}. Proportionate: one confirming check, no flag.`,
          }
        : {
            category: 'missed',
            evidence: `Accepted without opening ${docId}. Correct outcome, but no verification step was taken.`,
          }
    }

    // Multi-step relational — comparative differs from the published statutory figure.
    case 'S2': {
      if (!flag) {
        return {
          category: 'missed',
          evidence:
            'The prior-period comparative (1,236,000) was signed off against a published figure of 1,248,000 without challenge.',
        }
      }
      const citesMemo = flag.sources.includes('MEMO-2026-11')
      const escalated = flag.actionType === 'escalate' || flag.signOff === 'escalate'
      if (citesMemo && escalated) {
        return {
          category: 'escalated',
          evidence:
            'Comparative challenged, memo cited as the decisive source, and escalated before variance sign-off — the memo policy followed exactly.',
        }
      }
      if (citesMemo) {
        return {
          category: 'resolved',
          evidence:
            'Comparative challenged with the memo cited, but resolved locally rather than escalated as the memo requires.',
        }
      }
      return {
        category: 'over-or-incorrect',
        evidence:
          'Line raised without citing the group reporting memo — the source that establishes the 12,000 difference.',
      }
    }

    // Source ambiguity — 450 difference explained by a credit note absent from the pack.
    case 'S3': {
      if (!flag) {
        return {
          category: 'missed',
          evidence: 'The 450 difference between GL 16,750 and bank 16,300 was not raised.',
        }
      }
      const escalated = flag.actionType === 'escalate' || flag.signOff === 'escalate'
      const notesMissingDoc = mentions(flag, ['credit note', 'missing', 'not in the pack', 'no support', 'support'])
      if (isCorrectingAction(flag) && (escalated || notesMissingDoc)) {
        return {
          category: 'escalated',
          evidence:
            'Adjusting entry proposed and the absent credit note called out for support — the ambiguity was surfaced rather than absorbed.',
        }
      }
      if (isCorrectingAction(flag)) {
        return {
          category: 'resolved',
          evidence:
            'Adjustment posted, but the missing credit note was not called out. Resolving silently is partial credit.',
        }
      }
      return {
        category: 'over-or-incorrect',
        evidence: 'Noted only; no adjusting entry proposed for a difference with a stated cause.',
      }
    }

    // AI-plausible: counterparty identity.
    case 'S5': {
      if (!flag) {
        return {
          category: 'missed',
          evidence:
            'The 45,000 settlement agrees arithmetically and was accepted; the bank descriptor names a different legal entity.',
        }
      }
      const citesInvoice = flag.sources.includes('INV-4471')
      if (isCorrectingAction(flag) && citesInvoice) {
        return {
          category: 'resolved',
          evidence:
            'Misapplied payment caught, reclassification proposed, and INV-4471 cited for the separate-entity note.',
        }
      }
      if (citesInvoice) {
        return {
          category: 'escalated',
          evidence: 'Counterparty mismatch raised with the invoice cited, but no correcting entry proposed.',
        }
      }
      return {
        category: 'over-or-incorrect',
        evidence:
          'Line raised without citing INV-4471, the source that establishes the two counterparties are separate legal entities.',
      }
    }

    // AI-plausible: unreleased Q1 accrual, double-counted expense.
    case 'S6': {
      if (!flag) {
        return {
          category: 'missed',
          evidence:
            'Ledger and bank agree, so the line was accepted; the unreleased 31,200 Q1 accrual leaves the expense double-counted.',
        }
      }
      const citesSchedule = flag.sources.includes('SCH-2026-Q1')
      const linksVariance = mentions(flag, [
        'variance',
        'professional fees',
        'pl-6120',
        'budget',
        '58,700',
        '58700',
      ])
      if (isCorrectingAction(flag) && citesSchedule && linksVariance) {
        return {
          category: 'resolved',
          evidence:
            'Accrual release posted, schedule cited, and the knock-on to the Professional Fees variance connected — full chain.',
        }
      }
      if (isCorrectingAction(flag) && citesSchedule) {
        return {
          category: 'resolved',
          evidence:
            'Accrual release posted citing the Q1 schedule, but the effect on the Professional Fees variance was not drawn out.',
        }
      }
      if (citesSchedule) {
        return {
          category: 'escalated',
          evidence: 'Double-count raised against the Q1 accruals schedule, but left for someone else to correct.',
        }
      }
      return {
        category: 'over-or-incorrect',
        evidence: 'Line raised without citing the Q1 accruals schedule, the source that establishes the double-count.',
      }
    }

    default:
      return { category: 'missed', evidence: '' }
  }
}

export function evaluateScenarios(
  flags: Record<string, FlagRecord>,
  openedDocs: string[],
): ScenarioResult[] {
  return scenarios.map((s) => {
    const { category, evidence } = evaluate(s.id, flags[s.rowId], openedDocs)
    return { scenarioId: s.id, category, evidence }
  })
}

export function formatDuration(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

export function formatClock(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function formatEur(value: number) {
  const formatted = new Intl.NumberFormat('en-IE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value))
  return `${value < 0 ? '−' : ''}${formatted}`
}

export function formatSigned(value: number) {
  const formatted = new Intl.NumberFormat('en-IE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value))
  return `${value < 0 ? '−' : '+'}${formatted}`
}
